/**
 * A cross-browser compatible grid component.
 *
 * More about the grid in general: https://developer.mozilla.org/en-US/docs/Web/CSS/grid.
 *
 * The grid-template-areas section is especially useful for us: it's easy to adapt to other browsers and the
 * feature is very easy to understand.
 *
 * The CSS generated in these functions is IE11-friendly. A detailed analysis of what features are
 * and are not cross-browser-compatible for the grid can be found in this series of articles:
 * - https://css-tricks.com/css-grid-in-ie-debunking-common-ie-grid-misconceptions/
 * - https://css-tricks.com/css-grid-in-ie-css-grid-and-the-new-autoprefixer/
 * - https://css-tricks.com/css-grid-in-ie-faking-an-auto-placement-grid-with-gaps/
 *
 * In addition, the testcases for autoprefixer (https://github.com/postcss/autoprefixer) are
 * valuable. If we were not using a stylis.js-based CSS parser, we'd use autoprefixer to handle
 * these cases for us.
 */
import React from 'react';
import { AnyProps, JSXUnrendered } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import { css, cx } from '@tanium/react-emotion';
import { GridTemplateInputToken } from './grid-template-tokens';
import { Props as GridCellProps } from './GridCell';
import { GridGapConfig, gridTemplateToStyles } from './layout-helpers';

export interface GridBaseProps extends AnyProps {
  /**
   * GridCells to render.
   */
  children: (React.ReactElement<GridCellProps> | JSXUnrendered)[];
  /**
   * Any additional class which must be applied to the grid component
   */
  className?: string;
  /**
   * The component to which grid-styling should be applied. Defaults to `Box`. Use sparingly.
   */
  component?: React.ElementType;
  /**
   * Gap size between cells. A single value is applied to rows and columns; when receiving a tuple,
   * the grid treats the first entry in the tuple as a row gap and the second entry in the tuple as
   * a column gap. Defaults to 0.
   */
  gap?: GridGapConfig;
  /**
   * A grid of strings describing where an area should be rendered. An area is comprised of all the
   * cells of the templateAreas that have the same string identifier. Areas must be non-overlapping
   * rectangles.
   */
  templateAreas: string[][];
  /**
   * The sizing template for the rows. This may just be a series of lengths, or it may be composed
   * of shorthand notation (Repeat) or more flexible units (MinMax).
   */
  rowTemplate?: GridTemplateInputToken[];
  /**
   * The sizing template for the columns. This may just be a series of lengths, or it may be composed
   * of shorthand notation (Repeat) or more flexible units (MinMax).
   */
  columnTemplate?: GridTemplateInputToken[];
}

/**
 * Cross-browser friendly CSS grid component.
 *
 * This implements a subset of CSS grid features which work in all browsers. The API is designed to
 * be evocative of the CSS grid API. Not all features (e.g. 'auto' in 'repeat') are available, but
 * most functionality should be achievable with this component.
 */
export function GridBase({
  children,
  className,
  columnTemplate,
  component: Component = Box,
  gap,
  rowTemplate,
  templateAreas,
  ...rest
}: GridBaseProps) {
  const { childStyles, parentStyles } = gridTemplateToStyles(
    templateAreas,
    gap,
    rowTemplate,
    columnTemplate,
  );

  return (
    <Component className={cx(css(parentStyles), className)} {...rest}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement<GridCellProps>(child)) {
          return React.cloneElement(child, {
            cssMap: childStyles,
          });
        }
        return null;
      })?.filter(Boolean)}
    </Component>
  );
}
